/**
 * Gates the entire app chrome and router behind Keycloak auth.
 *
 * G2 §2.6 — "Empty, loading, error and no-permission states designed for every surface —
 * not left to the framework default." An unauthenticated or still-resolving visitor is
 * one of those designed surfaces, not a blank flash of chrome while a redirect happens
 * silently underneath it. `App.tsx` wraps the entire `AppChrome` + `Router` tree in this
 * component so nothing behind the gate ever mounts for a visitor Keycloak has not
 * authenticated — `/auth/callback` itself is the one screen exempt from this, mounted
 * standalone by `App.tsx` so the sign-in exchange has somewhere to land.
 *
 * Both states render through `SignInSurface`, which carries the identity photograph
 * design/09 §3 puts in chrome. They were a bare `EmptyState` and a bare `Skeleton` on
 * the page plane until ADR 0086: designed, in §2.6's sense, and anonymous in §3's.
 *
 * The two states are told apart rather than merged. "Still checking whether you have a
 * session" and "you do not have one" are different findings, and only the second has an
 * action attached to it — the same distinction §3.4 of CLAUDE.md draws between "no run
 * selected" and "this run has no proposals".
 */

import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useAuthSession, login } from '../../auth/session';
import { SignInPanel, SignInSurface } from '../chrome/SignInSurface';
import { Button, Skeleton } from '../../primitives';

export interface LoginGateProps {
  readonly children: React.ReactNode;
}

export function LoginGate({ children }: LoginGateProps): React.JSX.Element {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuthSession();

  if (isLoading) {
    return (
      <SignInSurface labelKey="auth.landmark">
        {/* `Skeleton` already carries role="status", aria-live and an sr-only
            `state.loading`, so the wait is announced without a second live region. */}
        <SignInPanel titleKey="auth.resolving.title" bodyKey="auth.resolving.body">
          <Skeleton rows={3} />
        </SignInPanel>
      </SignInSurface>
    );
  }

  if (!isAuthenticated) {
    return (
      <SignInSurface labelKey="auth.landmark">
        <SignInPanel titleKey="auth.signedOut.title" bodyKey="auth.signedOut.body">
          {/*
            ADR 9004 - the approved redesign's sign-in action, as brief part 1 §7.3 (DEC-95)
            confirms it: brand red (`primary`, #d40001), the FULL width of the panel column,
            the label pushed to the start and the arrow to the end. Wide because it is the
            only action on the screen, and a small button in an empty column reads as one
            option among several that are not there. It used to stop at `max-w-sm`; the
            column itself is capped at `max-w-lg`, which still leaves G6's 40% string
            expansion room ("Iniciar sesion con la cuenta Bata").
          */}
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              onClick={() => login()}
              size="lg"
              icon={<ArrowRight size={18} aria-hidden className="order-2" />}
              className="w-full justify-between"
            >
              {t('action.signIn')}
            </Button>
            {/* What kind of sign-in this is, before the planner presses it. Keycloak's own
                screen says so too, but only after the redirect has already happened. */}
            <p className="text-caption text-ink-secondary">{t('auth.signedOut.method')}</p>
          </div>
        </SignInPanel>
      </SignInSurface>
    );
  }

  return <>{children}</>;
}
